/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useEffect, useState, memo } from 'react';
import clsx from 'clsx';
import BaseUpload from 'epn-ui/umd/dist/components/Upload';
import Loader from 'epn-ui/umd/dist/components/Loader';
import Button from 'epn-ui/umd/dist/components/Button';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPaperclip } from '@fortawesome/pro-regular-svg-icons/faPaperclip';
import { faCheckCircle } from '@fortawesome/pro-regular-svg-icons/faCheckCircle';
import { faTimes } from '@fortawesome/pro-regular-svg-icons/faTimes';
import { faExclamationCircle } from '@fortawesome/pro-regular-svg-icons/faExclamationCircle';
import { faImage } from '@fortawesome/pro-regular-svg-icons/faImage';

import useI18n from '@i18n';

import styles from './BaseDragAndDrop.module.scss';

enum STATUS {
    waiting,
    hover,
    loading,
    success,
    error,
}

interface IBaseDragAndDropProps {
    allowedTypes: string;
    maxFilesSizeMb: number;
    error: string;
    onChange: (item: File[]) => void;
    isLoading: boolean;
    multiple: boolean;
}

const validateSize = (
    file: File,
    maxFilesSizeMb: IBaseDragAndDropProps['maxFilesSizeMb'],
): boolean => {
    const sizeInBytes = file.size;
    if (sizeInBytes < Number(maxFilesSizeMb) * 1024 * 1024) {
        return true;
    }
    return false;
};

const validateTypes = (
    file: File,
    allowedTypes: IBaseDragAndDropProps['allowedTypes'],
): boolean => {
    return allowedTypes.includes(file.type);
};

const BaseDragAndDrop: React.FC<IBaseDragAndDropProps> = ({
    allowedTypes,
    maxFilesSizeMb,
    error,
    onChange,
    multiple,
    isLoading,
}) => {
    const [status, setStatus] = useState<STATUS>(STATUS.waiting);
    const [fileList, setFileList] = useState([]);
    const [errorMessage, setErrorMessage] = useState<string>('');
    const { t } = useI18n('Base');
    const { t: tErrors } = useI18n('Errors');

    useEffect((): void => {
        if (error) {
            setErrorMessage(error);
            setStatus(STATUS.error);
        }
    }, [error]);

    useEffect((): void => {
        if (!isLoading && status === STATUS.loading) {
            setStatus(STATUS.waiting);
        }
        if (isLoading && status !== STATUS.loading) {
            setStatus(STATUS.loading);
        }
    }, [isLoading, status]);

    const validateFile = file => {
        try {
            setStatus(STATUS.loading);
            // Проверяю разрешенные форматы файла
            if (!validateTypes(file, allowedTypes)) {
                setErrorMessage(
                    tErrors('Invalid format. Allowed formats are: {{allowedTypes}}', {
                        allowedTypes,
                    }),
                );
                setStatus(STATUS.error);

                return false;
            }
            // Проверяю максимальный размер файла
            if (!validateSize(file, maxFilesSizeMb)) {
                setErrorMessage(
                    tErrors('The file is too large. The allowable size is {{size}} MB', {
                        size: maxFilesSizeMb,
                    }),
                );
                setStatus(STATUS.error);

                return false;
            }

            return false;
        } catch (err) {
            console.error(err);
            setErrorMessage(tErrors('An unknown error occurred. Please try again'));
            setStatus(STATUS.error);

            return false;
        }
    };

    const filesHandler = (items): void => {
        try {
            if (items.length > 1 && !multiple) {
                setErrorMessage(tErrors('Maximum 1 file'));
                setStatus(STATUS.error);
                return;
            }
            onChange(items);
            setFileList(items);
            setStatus(STATUS.success);
        } catch (err: unknown) {
            console.error(err);
            setErrorMessage(
                err instanceof Error
                    ? err.message
                    : tErrors('An unknown error occurred. Please try again'),
            );
            setStatus(STATUS.error);
        }
    };

    const dragEnterHandler = (e: React.DragEvent<HTMLDivElement>): void => {
        e.preventDefault();
        if (status === STATUS.hover) return;
        setStatus(STATUS.hover);
    };

    const dragLeaveHandler = (e: React.DragEvent<HTMLDivElement>): void => {
        e.preventDefault();
        if (e.currentTarget.contains(e.relatedTarget as Node)) return;
        setStatus(STATUS.waiting);
    };

    const changeHandler = file => {
        if (status === STATUS.error) return false;
        filesHandler(Array.from(file.fileList));

        return false;
    };

    const onRemoveFile = (e, fileIndex: number): void => {
        e.stopPropagation();
        const newFiles = fileList.filter((_item, index) => fileIndex !== index);
        onChange(newFiles);
        setFileList(newFiles);

        if (newFiles.length === 0) {
            setStatus(STATUS.waiting);
        }
    };

    const onRepeat = (): void => {
        onChange([]);
        setErrorMessage('');
        setStatus(STATUS.waiting);
    };

    return (
        <div onDragEnter={dragEnterHandler} onDragLeave={dragLeaveHandler}>
            <BaseUpload.Dragger
                className={clsx(styles.dropArea, {
                    [styles.dropArea__hover]: status === STATUS.hover,
                })}
                onChange={changeHandler}
                beforeUpload={validateFile}
                showUploadList={false}
                multiple
                fileList={fileList}
            >
                <>
                    {status === STATUS.waiting && (
                        <div className={clsx(styles.inner, styles.inner__withHover)}>
                            <FontAwesomeIcon icon={faPaperclip} className={styles.icon} />
                            <p className={clsx(styles.primaryText, styles.title)}>
                                {t('Attach image')}
                            </p>

                            <div className={styles.body}>
                                <p className={styles.secondaryText}>{t('Drag image here')}</p>&nbsp;
                                <div className={styles.manualFiles}>
                                    <span className={styles.largeText}>
                                        {t('Choose a file manually')}
                                    </span>
                                    <span className={styles.shortText}>{t('Choose')}</span>
                                </div>
                            </div>

                            <div className={styles.attachInfo}>
                                <p className={styles.secondaryText}>
                                    {t('Available file formats:')}{' '}
                                    {allowedTypes
                                        .split(',')
                                        .map(item => item.split('/')[1].toUpperCase())
                                        .join(', ')}
                                </p>
                                <p className={styles.secondaryText}>
                                    {t('Size: up to {{maxSize}} MB', { maxSize: maxFilesSizeMb })}
                                </p>
                            </div>
                        </div>
                    )}
                    {status === STATUS.hover && (
                        <div className={styles.inner}>
                            <FontAwesomeIcon icon={faImage} className={styles.icon} />
                            <p className={clsx(styles.primaryText, styles.title)}>
                                {t('Drop to add an image')}
                            </p>
                        </div>
                    )}
                    {status === STATUS.loading && (
                        <div className={styles.inner}>
                            <Loader />
                            <p className={clsx(styles.primaryText, styles.title)}>
                                {t('The image is being added')}
                            </p>
                        </div>
                    )}
                    {status === STATUS.success && (
                        <div className={styles.inner}>
                            <FontAwesomeIcon
                                icon={faCheckCircle}
                                className={clsx(styles.icon, styles.icon__success)}
                            />
                            <p className={clsx(styles.primaryText, styles.title)}>
                                {t('Image uploaded')}
                            </p>
                            <p className={clsx(styles.secondaryText, styles.subtitle)}>
                                {t(
                                    'You can delete it or upload a new image to replace the uploaded one',
                                )}
                            </p>
                            <div className={styles.files}>
                                <ul>
                                    {fileList.length > 0 &&
                                        fileList.map((item, index: number) => (
                                            <li
                                                className={styles.file}
                                                key={`${item.name}${index}`}
                                            >
                                                <FontAwesomeIcon icon={faPaperclip} />
                                                &nbsp;
                                                {item.name}
                                                <FontAwesomeIcon
                                                    icon={faTimes}
                                                    className={styles.remove}
                                                    onClick={e => onRemoveFile(e, index)}
                                                />
                                            </li>
                                        ))}
                                </ul>
                            </div>
                        </div>
                    )}
                    {status === STATUS.error && (
                        <div className={styles.inner}>
                            <FontAwesomeIcon
                                icon={faExclamationCircle}
                                className={clsx(styles.icon, styles.icon__error)}
                            />
                            <p
                                className={clsx(
                                    styles.primaryText,
                                    styles.title,
                                    styles.title__error,
                                )}
                            >
                                {errorMessage}
                            </p>
                            <Button className={styles.btn} type="primary" onClick={onRepeat}>
                                {t('Try again')}
                            </Button>
                        </div>
                    )}
                </>
            </BaseUpload.Dragger>
        </div>
    );
};

export default memo(BaseDragAndDrop);
